import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, categoriesMap } from '@/mocks/categories';
import { getMangaByGenre } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoriesMap[slug] : undefined;
  const mangas = slug ? getMangaByGenre(category?.name || '') : [];

  useEffect(() => {
    if (!category) return;
    document.title = `${category.name}漫画 - 汗汗漫画 | 免费韩国漫画在线阅读`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', `汗汗漫画${category.name}分类——${category.description}。汇集海量${category.name}题材韩国漫画，免费在线阅读，每日更新。`);
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `http://sinotruckcn.com/category/${slug}`);
    }

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${category.name}漫画 - 汗汗漫画`,
      description: `${category.description}。汗汗漫画免费韩国漫画在线阅读。`,
      url: `http://sinotruckcn.com/category/${slug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '汗汗漫画',
        url: 'http://sinotruckcn.com/',
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'http://sinotruckcn.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: `${category.name}漫画`,
          item: `http://sinotruckcn.com/category/${slug}`,
        },
      ],
    };
    const scriptBreadcrumb = document.createElement('script');
    scriptBreadcrumb.type = 'application/ld+json';
    scriptBreadcrumb.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(scriptBreadcrumb);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      if (document.head.contains(scriptBreadcrumb)) {
        document.head.removeChild(scriptBreadcrumb);
      }
    };
  }, [slug, category]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 mb-2">分类未找到</h1>
            <p className="text-foreground-600 mb-6">该分类不存在或已被移除</p>
            <Link to="/" className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <nav className="w-full px-6 md:px-10 py-4 bg-background-50 border-b border-background-200" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm">
            <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors duration-200">首页</Link>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
            </div>
            <span className="text-foreground-900 font-medium">{category.name}漫画</span>
          </div>
        </nav>

        <section className="w-full py-10 md:py-14 px-6 md:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  {category.name}漫画
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {mangas.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-book-2-line text-4xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 md:gap-6">
                {mangas.map((manga) => (
                  <Link
                    key={manga.id}
                    to={`/manga/${manga.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-200 mb-3">
                      <img
                        src={manga.coverImage}
                        alt={manga.title}
                        title={`${manga.title} - 汗汗漫画免费韩国漫画`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      {manga.status === '连载中' && (
                        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-accent-500 text-foreground-950 text-xs font-bold">
                          连载
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-bold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors duration-200">
                      {manga.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="flex items-center gap-0.5">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-star-fill text-xs text-accent-500"></i>
                        </div>
                        <span className="text-xs font-semibold text-foreground-600">{manga.rating}</span>
                      </div>
                      <span className="text-xs text-foreground-400">
                        {(manga.viewCount / 10000).toFixed(0)}万
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}